import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  Checkbox,
  Input,
  Modal,
  Select,
  Spin,
  Switch,
  Table,
  Typography,
  message,
} from "antd";
import { GlobalOutlined, LockOutlined, PlusOutlined, SearchOutlined } from "@ant-design/icons";
import { getSelectedDivisionId } from "app/shared/division/selectedDivisionStorage";
import {
  loadCountries,
  loadRegions,
  loadDivisions,
  loadInheritance,
  saveInheritance,
  setInherit,
  setSourceDivisionId,
  setCountryRegion,
  setBulkRegion,
  selectCountries,
  selectAll,
  unselectAll,
  setSearch,
  discardChanges,
  openViewRegions,
  closeViewRegions,
  saveCountryRegions,
  setSort,
} from "./actions";
import {
  selectCountries as selectCountriesList,
  selectRegions,
  selectCountriesLoading,
  selectRegionsLoading,
  selectInheritFromDivision,
  selectSourceDivisionId,
  selectPersistedInheritedFromDivisionId,
  selectCountryEdits,
  selectSelectedCountryIds,
  selectSearchTerm,
  selectViewRegionsModalOpen,
  selectSaving,
  selectSaveError,
  selectHasUnsavedChanges,
  selectAllCountriesHaveRegion,
  selectSortBy,
  selectSortOrder,
  selectSaveSuccessUnassigned,
} from "./selectors";
import type { CountryWithRegionDto } from "app/shared/api/getCountriesWithRegions";
import type { RegionDto } from "app/shared/api/getRegions";
import { createRegion, updateRegion } from "app/shared/api/getRegions";
import ValidationWarningModal from "app/shared/ui/ValidationWarningModal";
import "./RegionManager.css";

const { Title, Text } = Typography;

const PAGE_TITLE = "Region Manager";
const PLACEHOLDER_REGION = "Select Region";
type RegionFilterValue = "all" | "unassigned" | number;

const RegionManagerPage: React.FC = () => {
  const dispatch = useDispatch<any>();
  const [divisions, setDivisions] = useState<Array<{ id: number; name: string }>>([]);
  const [confirmModal, setConfirmModal] = useState<
    "inherit-discard" | "cancel-discard" | "unassigned" | null
  >(null);
  const [regionFilter, setRegionFilter] = useState<RegionFilterValue>("all");
  const tableAreaRef = React.useRef<HTMLDivElement>(null);
  const footerRef = React.useRef<HTMLDivElement>(null);
  const [tableAreaHeight, setTableAreaHeight] = useState(() =>
    typeof window !== "undefined"
      ? Math.max(400, Math.floor((window.innerHeight - 50) * 0.8))
      : 480
  );
  const [tableScrollY, setTableScrollY] = useState(400);

  const countriesRaw = useSelector(selectCountriesList);
  const regions = useSelector(selectRegions);
  const countriesLoading = useSelector(selectCountriesLoading);
  const regionsLoading = useSelector(selectRegionsLoading);
  const inherit = useSelector(selectInheritFromDivision);
  const sourceDivisionId = useSelector(selectSourceDivisionId);
  const persistedInheritedFromDivisionId = useSelector(selectPersistedInheritedFromDivisionId);
  const countryEdits = useSelector(selectCountryEdits);
  const selectedIds = useSelector(selectSelectedCountryIds);
  const searchTerm = useSelector(selectSearchTerm);
  const viewRegionsOpen = useSelector(selectViewRegionsModalOpen);
  const saving = useSelector(selectSaving);
  const saveError = useSelector(selectSaveError);
  const saveSuccessUnassigned = useSelector(selectSaveSuccessUnassigned);
  const hasUnsavedChanges = useSelector(selectHasUnsavedChanges);
  const allHaveRegion = useSelector(selectAllCountriesHaveRegion);
  const sortBy = useSelector(selectSortBy);
  const sortOrder = useSelector(selectSortOrder);
  const regionOptionsWithPlaceholder = useRegionOptions(regions);
  const regionOptionsWithoutPlaceholder = useRegionOptions(regions, false);

  const filteredCountries = useMemo(() => {
    const countries = countriesRaw;
    const term = (searchTerm ?? "").trim().toLowerCase();
    const edits = countryEdits ?? {};
    let list = term
      ? countries.filter((c) => (c.name ?? "").toLowerCase().includes(term))
      : [...countries];
    const effectiveRegionId = (c: CountryWithRegionDto) => {
      const key = String(c.id);
      return key in edits ? edits[key] : c.regionId;
    };
    if (regionFilter !== "all") {
      if (regionFilter === "unassigned") {
        list = list.filter((c) => effectiveRegionId(c) == null);
      } else {
        list = list.filter((c) => effectiveRegionId(c) === regionFilter);
      }
    }
    if (sortBy === "country") {
      list = [...list].sort((a, b) => {
        const cmp = (a.name ?? "").localeCompare(b.name ?? "");
        return sortOrder === "asc" ? cmp : -cmp;
      });
    } else {
      list = [...list].sort((a, b) => {
        const aAssigned = effectiveRegionId(a) != null ? 1 : 0;
        const bAssigned = effectiveRegionId(b) != null ? 1 : 0;
        if (aAssigned !== bAssigned) return aAssigned - bAssigned;
        const cmp = (a.name ?? "").localeCompare(b.name ?? "");
        return sortOrder === "asc" ? cmp : -cmp;
      });
    }
    return list;
  }, [countriesRaw, searchTerm, countryEdits, sortBy, sortOrder, regionFilter]);

  const [currentDivisionId, setCurrentDivisionId] = useState<number | null>(() => {
    const id = getSelectedDivisionId();
    return id > 0 ? id : null;
  });

  useEffect(() => {
    const onDivisionChanged = () => {
      const id = getSelectedDivisionId();
      const next = id > 0 ? id : null;
      setCurrentDivisionId((prev) => (prev !== next ? next : prev));
    };
    window.addEventListener('selectedDivisionChanged', onDivisionChanged);
    return () => window.removeEventListener('selectedDivisionChanged', onDivisionChanged);
  }, []);

  const prevDivisionIdRef = React.useRef<number | null>(currentDivisionId);
  useEffect(() => {
    if (prevDivisionIdRef.current !== currentDivisionId) {
      prevDivisionIdRef.current = currentDivisionId;
      dispatch(discardChanges());
    }
  }, [currentDivisionId, dispatch]);

  useEffect(() => {
    dispatch(loadDivisions()).then(setDivisions);
  }, [dispatch]);

  useEffect(() => {
    if (currentDivisionId) dispatch(loadInheritance(currentDivisionId));
  }, [dispatch, currentDivisionId]);

  useEffect(() => {
    const divisionId = inherit && sourceDivisionId != null ? sourceDivisionId : (currentDivisionId ?? 0);
    if (divisionId) dispatch(loadCountries(divisionId));
  }, [dispatch, inherit, sourceDivisionId, currentDivisionId]);

  useEffect(() => {
    const divisionId = inherit && sourceDivisionId != null ? sourceDivisionId : (currentDivisionId ?? 0);
    if (divisionId) dispatch(loadRegions(divisionId));
  }, [dispatch, inherit, sourceDivisionId, currentDivisionId]);

  useEffect(() => {
    if (regionFilter === "all" || regionFilter === "unassigned") return;
    if (!regions.some((r) => r.id === regionFilter)) setRegionFilter("all");
  }, [regions, regionFilter]);

  const isReadOnly = inherit;

  useEffect(() => {
    if (!hasUnsavedChanges || isReadOnly) return;
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [hasUnsavedChanges, isReadOnly]);

  const selectedCount = selectedIds.length;
  const showBulkBar = selectedCount > 0 && !isReadOnly;

  useEffect(() => {
    const updateTableLayout = () => {
      const area = tableAreaRef.current;
      if (!area) return;

      const pageHeight = window.innerHeight - 50;
      const top = area.getBoundingClientRect().top;
      const footerH = footerRef.current?.getBoundingClientRect().height ?? 44;
      const extra =
        (saveError ? 28 : 0) +
        (saveSuccessUnassigned.length > 0 ? 48 : 0) +
        16;
      const available = Math.floor(window.innerHeight - top - footerH - extra);
      const height = Math.max(240, Math.min(Math.floor(pageHeight * 0.8), available));
      setTableAreaHeight((prev) => (prev === height ? prev : height));

      const header = area.querySelector(
        ".ant-table-header, thead.ant-table-thead, .ant-table-thead"
      ) as HTMLElement | null;
      const headerHeight = header?.getBoundingClientRect().height || 39;
      const scrollY = Math.max(200, Math.floor(height - headerHeight));
      setTableScrollY((prev) => (prev === scrollY ? prev : scrollY));
    };

    const rafUpdate = () => requestAnimationFrame(updateTableLayout);
    rafUpdate();
    const t1 = window.setTimeout(rafUpdate, 50);
    const t2 = window.setTimeout(rafUpdate, 200);
    window.addEventListener("resize", rafUpdate);
    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      window.removeEventListener("resize", rafUpdate);
    };
  }, [
    countriesLoading,
    filteredCountries.length,
    showBulkBar,
    saveError,
    saveSuccessUnassigned.length,
    inherit,
  ]);

  const sourceDivisionOptions = useMemo(
    () => divisions.filter((d) => d.id !== currentDivisionId),
    [divisions, currentDivisionId]
  );

  const defaultSourceDivisionId = useCallback(() => {
    if (persistedInheritedFromDivisionId != null) return persistedInheritedFromDivisionId;
    return sourceDivisionOptions[0]?.id ?? null;
  }, [persistedInheritedFromDivisionId, sourceDivisionOptions]);

  const inheritanceDirty = useMemo(() => {
    if (inherit) {
      return sourceDivisionId != null && sourceDivisionId !== persistedInheritedFromDivisionId;
    }
    return persistedInheritedFromDivisionId != null;
  }, [inherit, sourceDivisionId, persistedInheritedFromDivisionId]);

  const canSave = inherit ? inheritanceDirty : hasUnsavedChanges || inheritanceDirty;

  const handleInheritChange = useCallback(
    (checked: boolean) => {
      if (checked && hasUnsavedChanges) {
        setConfirmModal("inherit-discard");
      } else {
        dispatch(setInherit(checked));
        if (checked && !sourceDivisionId) {
          const next = defaultSourceDivisionId();
          if (next != null) dispatch(setSourceDivisionId(next));
        } else if (!checked) {
          dispatch(setSourceDivisionId(null));
          if (currentDivisionId != null) dispatch(loadCountries(currentDivisionId));
          dispatch(discardChanges());
        }
      }
    },
    [dispatch, hasUnsavedChanges, sourceDivisionId, defaultSourceDivisionId, currentDivisionId]
  );

  const handleSourceDivisionChange = useCallback(
    (divisionId: number | null) => {
      dispatch(setSourceDivisionId(divisionId));
      if (divisionId != null) dispatch(loadCountries(divisionId));
    },
    [dispatch]
  );

  const handleRowSelect = useCallback(
    (countryId: string | number, checked: boolean) => {
      const idStr = String(countryId);
      if (checked) {
        dispatch(selectCountries([...selectedIds.filter((id) => id !== idStr), idStr]));
      } else {
        dispatch(selectCountries(selectedIds.filter((id) => id !== idStr)));
      }
    },
    [dispatch, selectedIds]
  );

  const handleSelectAll = useCallback(
    (checked: boolean) => {
      if (checked) {
        dispatch(selectAll(filteredCountries.map((c) => c.id)));
      } else {
        dispatch(unselectAll());
      }
    },
    [dispatch, filteredCountries]
  );

  const handleHeaderCheckboxDblClick = useCallback(() => {
    dispatch(unselectAll());
  }, [dispatch]);

  const handleBulkRegionChange = useCallback(
    (regionId: number | null) => {
      if (selectedIds.length === 0) return;
      dispatch(setBulkRegion(selectedIds, regionId));
    },
    [dispatch, selectedIds]
  );

  const resetInheritanceSelection = useCallback(() => {
    dispatch(setInherit(persistedInheritedFromDivisionId != null));
    dispatch(setSourceDivisionId(persistedInheritedFromDivisionId));
  }, [dispatch, persistedInheritedFromDivisionId]);

  const handleSave = useCallback(() => {
    if (!currentDivisionId) return;

    if (inherit) {
      if (sourceDivisionId != null && sourceDivisionId !== persistedInheritedFromDivisionId) {
        dispatch(saveInheritance(currentDivisionId, sourceDivisionId));
      }
      return;
    }

    if (persistedInheritedFromDivisionId != null) {
      dispatch(saveInheritance(currentDivisionId, null));
    }

    const dirtyRows: Array<{ countryId: string | number; regionId: number }> = [];
    const edits = countryEdits;
    for (const c of countriesRaw) {
      const key = String(c.id);
      if (!(key in edits)) continue;
      const regionId = edits[key];
      if (regionId != null) dirtyRows.push({ countryId: c.id, regionId });
    }
    if (dirtyRows.length === 0) return;
    dispatch(saveCountryRegions(dirtyRows));
  }, [
    dispatch,
    currentDivisionId,
    inherit,
    sourceDivisionId,
    persistedInheritedFromDivisionId,
    countryEdits,
    countriesRaw,
  ]);

  const handleCancel = useCallback(() => {
    if (canSave) setConfirmModal("cancel-discard");
    else dispatch(discardChanges());
  }, [dispatch, canSave]);

  const handleConfirmInheritDiscard = useCallback(() => {
    setConfirmModal(null);
    dispatch(setInherit(true));
    dispatch(discardChanges());
    const next = defaultSourceDivisionId();
    if (next != null) {
      dispatch(setSourceDivisionId(next));
      dispatch(loadCountries(next));
    }
  }, [dispatch, defaultSourceDivisionId]);

  const handleConfirmCancelDiscard = useCallback(() => {
    setConfirmModal(null);
    resetInheritanceSelection();
    dispatch(discardChanges());
  }, [dispatch, resetInheritanceSelection]);


  function useRegionOptions(regions: RegionDto[], includePlaceholder = true) {
    return useMemo(() => {
      const base = regions.map((r) => ({
        value: r.id,
        label: r.name,
      }));

      return includePlaceholder
        ? [{ value: null as number | null, label: PLACEHOLDER_REGION }, ...base]
        : base;
    }, [regions, includePlaceholder]);
  }

  const regionFilterOptions = useMemo(
    () => [
      { value: "all" as const, label: "All regions" },
      { value: "unassigned" as const, label: "Unassigned" },
      ...regions.map((r) => ({ value: r.id, label: r.name })),
    ],
    [regions]
  );

  const getEffectiveRegionId = useCallback(
    (record: CountryWithRegionDto) => {
      const key = String(record.id);
      return key in countryEdits ? countryEdits[key] : record.regionId;
    },
    [countryEdits]
  );

  const columns = useMemo(() => {
    const countrySortOrder =
      sortBy === "country" ? (sortOrder === "asc" ? "ascend" : "descend") : undefined;
    const regionSortOrder =
      sortBy === "region" ? (sortOrder === "asc" ? "ascend" : "descend") : undefined;
    return [
      {
        title: (
          <span onDoubleClick={handleHeaderCheckboxDblClick}>
            <Checkbox
              checked={
                filteredCountries.length > 0 &&
                filteredCountries.every((c) => selectedIds.includes(String(c.id)))
              }
              indeterminate={
                selectedCount > 0 && selectedCount < filteredCountries.length
              }
              onChange={(e) => handleSelectAll(e.target.checked)}
              disabled={isReadOnly}
            />
          </span>
        ),
        key: "checkbox",
        width: 48,
        render: (_: any, record: CountryWithRegionDto) => {
          const idStr = String(record.id);
          return (
            <Checkbox
              checked={selectedIds.includes(idStr)}
              onChange={(e) => handleRowSelect(record.id, e.target.checked)}
              disabled={isReadOnly}
            />
          );
        },
      },
      {
        title: (
          <span
            style={{ cursor: "pointer" }}
            onClick={() =>
              dispatch(setSort("country", sortBy === "country" && sortOrder === "asc" ? "desc" : "asc"))
            }
          >
            Country {sortBy === "country" && (sortOrder === "asc" ? "↑" : "↓")}
          </span>
        ),
        dataIndex: "name",
        key: "country",
        render: (name: string) => name || "—",
      },
      {
        title: (
          <span
            style={{ cursor: "pointer" }}
            onClick={() =>
              dispatch(setSort("region", sortBy === "region" && sortOrder === "asc" ? "desc" : "asc"))
            }
          >
            Region {sortBy === "region" && (sortOrder === "asc" ? "↑" : "↓")}
          </span>
        ),
        key: "region",
        render: (_: any, record: CountryWithRegionDto) => {
          const effectiveRegionId = getEffectiveRegionId(record);
          if (isReadOnly) {
            const regionName =
              regions.find((r) => r.id === effectiveRegionId)?.name ?? "—";
            return (
              <span className="region-manager-region-cell-readonly">
                {regionName}
                <LockOutlined className="region-manager-region-cell-lock" />
              </span>
            );
          }
          if (showBulkBar) {
            const regionName =
              regions.find((r) => r.id === effectiveRegionId)?.name ?? PLACEHOLDER_REGION;
            return <span>{regionName}</span>;
          }
          return (
            <Select
              className="region-manager-row-region-select"
              value={effectiveRegionId}
              placeholder={PLACEHOLDER_REGION}
              //allowClear
              options={effectiveRegionId !== null && effectiveRegionId > 0 ? regionOptionsWithoutPlaceholder : regionOptionsWithPlaceholder}
              onChange={(val) => dispatch(setCountryRegion(record.id, val))}
              status={effectiveRegionId == null ? "error" : undefined}
            />
          );
        },
      },
    ];
  }, [
    filteredCountries,
    selectedIds,
    selectedCount,
    isReadOnly,
    showBulkBar,
    regions,
    regionOptionsWithPlaceholder,
    regionOptionsWithoutPlaceholder,
    sortBy,
    sortOrder,
    getEffectiveRegionId,
    handleSelectAll,
    handleHeaderCheckboxDblClick,
    handleRowSelect,
    dispatch,
  ]);

  return (
    <div className="region-manager-page">
      <Title level={3} className="region-manager-title">
        {PAGE_TITLE}
      </Title>

      <div className="region-manager-inherit-section">
        <div className="region-manager-inherit-toggle">
          <Switch checked={inherit} onChange={handleInheritChange} />
          <div>
            <Text strong>Inherit from other division</Text>
            <div className="region-manager-inherit-desc">
              {inherit
                ? "Read only - using regions from the selected division."
                : "Edit and save this division's own region mapping."}
            </div>
          </div>
        </div>
        {inherit && (
          <div className="region-manager-source-dropdown">
            <Text type="secondary">Source: </Text>
            <Select
              className="region-manager-source-select"
              value={sourceDivisionId}
              onChange={handleSourceDivisionChange}
              placeholder="Select division"
              options={sourceDivisionOptions.map((d) => ({ value: d.id, label: d.name }))}
            />
          </div>
        )}
      </div>

      <div className="region-manager-toolbar">
        <Input.Search
          placeholder="Search by Country"
          allowClear
          value={searchTerm}
          onChange={(e) => dispatch(setSearch(e.target.value))}
          style={{ width: 260 }}
          prefix={<SearchOutlined />}
        />
        <Select
          value={regionFilter}
          onChange={(val) => setRegionFilter(val)}
          placeholder="Filter by Region"
          options={regionFilterOptions}
          style={{ width: 220 }}
          disabled={regionsLoading || regions.length === 0}
        />
        {!isReadOnly && (
          <Button
            className="region-manager-manage-regions-btn"
            icon={<GlobalOutlined />}
            onClick={() => dispatch(openViewRegions())}
          >
            Manage Regions
          </Button>
        )}
      </div>

      {showBulkBar && (
        <div className="region-manager-selected-bar">
          <span>selected: {selectedCount}</span>
          <Select
            className="region-manager-bulk-select"
            placeholder="Select Region"
            allowClear
            options={regionOptionsWithPlaceholder.filter((o) => o.value != null)}
            onChange={handleBulkRegionChange}
          />
        </div>
      )}

      <div
        ref={tableAreaRef}
        className="region-manager-table-area"
        style={{ height: tableAreaHeight, flex: "0 0 auto" }}
      >
        <Spin spinning={countriesLoading}>
          <div className="region-manager-table-wrapper">
            <Table
              rowKey={(r) => String(r.id)}
              columns={columns}
              dataSource={filteredCountries}
              pagination={false}
              scroll={{ y: tableScrollY }}
              size="small"
            />
          </div>
        </Spin>
      </div>

      {saveError && (
        <Text type="danger" className="region-manager-save-error">
          {saveError}
        </Text>
      )}

      {saveSuccessUnassigned.length > 0 && (
        <div className="region-manager-save-success-unassigned">
          <div className="region-manager-unassigned-list">
            <Text strong type="secondary">Unassigned countries: </Text>
            <Text type="secondary">
              {saveSuccessUnassigned.map((c) => c.countryName || c.countryCode).join(", ")}
            </Text>
          </div>
        </div>
      )}

      <div ref={footerRef} className="region-manager-footer">
        <Button onClick={handleCancel} disabled={saving || !canSave}>
          Cancel
        </Button>
        <Button
          type="primary"
          onClick={handleSave}
          loading={saving}
          disabled={saving || !canSave}
        >
          Save
        </Button>
      </div>

      <ValidationWarningModal
        open={confirmModal === "unassigned"}
        title="Unassigned countries"
        message="You still have countries without a region. Assign all to a region to save."
        onCancel={() => setConfirmModal(null)}
        onOk={() => setConfirmModal(null)}
      />

      <ValidationWarningModal
        open={confirmModal === "cancel-discard"}
        title="Unsaved changes"
        message="You have unsaved changes. Continue without saving?"
        onCancel={() => setConfirmModal(null)}
        onOk={handleConfirmCancelDiscard}
      />

      <ValidationWarningModal
        open={confirmModal === "inherit-discard"}
        title="Turn on inheritance?"
        message="Turn on inheritance and discard unsaved changes?"
        onCancel={() => setConfirmModal(null)}
        onOk={handleConfirmInheritDiscard}
      />

      {viewRegionsOpen && (
        <ViewRegionsModal
          divisionId={currentDivisionId ?? 0}
          regions={regions}
          onClose={() => dispatch(closeViewRegions())}
          onSaved={() => {
            dispatch(closeViewRegions());
            if (currentDivisionId) dispatch(loadRegions(currentDivisionId));
          }}
        />
      )}
    </div>
  );
};

interface ViewRegionsModalProps {
  divisionId: number;
  regions: RegionDto[];
  onClose: () => void;
  onSaved: () => void;
}

const ViewRegionsModal: React.FC<ViewRegionsModalProps> = ({ divisionId, regions, onClose, onSaved }) => {
  const [list, setList] = useState<Array<{ id?: number; name: string; error?: string }>>([]);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false);

  useEffect(() => {
    setList(regions.map((r) => ({ id: r.id, name: r.name ?? "" })));
    setHasChanges(false);
  }, [regions]);

  const addRow = () => {
    setList((prev) => [...prev, { name: "" }]);
    setHasChanges(true);
  };
  const updateName = (index: number, name: string) => {
    setList((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], name: name.trim() || "", error: undefined };
      return next;
    });
    setHasChanges(true);
  };

  const validate = (): boolean => {
    const names = list.map((item) => (item.name ?? "").trim().toLowerCase());
    const seen = new Set<string>();
    let valid = true;
    const nextList = list.map((item, i) => {
      const name = (item.name ?? "").trim();
      let error: string | undefined;
      if (name.length < 2) error = "Min 2 characters";
      else if (name.length > 20) error = "Max 20 characters";
      else {
        const key = name.toLowerCase();
        if (seen.has(key)) {
          error = "Region name exists";
          valid = false;
        } else seen.add(key);
      }
      return { ...item, error };
    });
    setList(nextList);
    return valid && nextList.every((item) => !item.error);
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const originalNameById = new Map(
        regions.map((r) => [r.id, (r.name ?? "").trim()])
      );
      for (const item of list) {
        const name = (item.name ?? "").trim();
        if (!name) continue;
        if (item.id != null) {
          const previous = originalNameById.get(item.id) ?? "";
          if (name !== previous) {
            await updateRegion(item.id, name);
          }
        } else {
          await createRegion(name, divisionId);
        }
      }
      onSaved();
      message.success("Regions saved");
      setHasChanges(false);
      onClose();
    } catch (e: any) {
      message.error(e?.message ?? "Failed to save regions");
    } finally {
      setSaving(false);
    }
  };

  const handleRequestClose = () => {
    if (hasChanges) {
      setConfirmDiscardOpen(true);
    } else {
      onClose();
    }
  };

  const handleConfirmDiscardViewRegions = () => {
    setConfirmDiscardOpen(false);
    onClose();
  };

  const handleStayViewRegions = () => {
    setConfirmDiscardOpen(false);
  };

  return (
    <>
      <Modal
        title="Added Regions"
        open
        onCancel={handleRequestClose}
        onOk={handleSave}
        okText="Save"
        cancelText="Cancel"
        confirmLoading={saving}
        okButtonProps={{ disabled: !hasChanges }}
        width={480}
        zIndex={1000}
        destroyOnHidden
      >
      <div className="region-manager-view-regions-list">
        {list.map((item, index) => (
          <div key={index}>
            <Input
              value={item.name}
              onChange={(e) => updateName(index, e.target.value)}
              placeholder="Region name"
              status={item.error ? "error" : undefined}
              maxLength={20}
            />
            {item.error && (
              <Text type="danger" style={{ fontSize: 12 }}>
                {item.error}
              </Text>
            )}
          </div>
        ))}
      </div>
      <Button type="dashed" onClick={addRow} icon={<PlusOutlined />} block>
        Add Region
      </Button>
    </Modal>

      <ValidationWarningModal
        open={confirmDiscardOpen}
        title="Unsaved changes"
        message="You have unsaved changes. Continue without saving?"
        onCancel={handleStayViewRegions}
        onOk={handleConfirmDiscardViewRegions}
        zIndex={1100}
      />
    </>
  );
};

export default RegionManagerPage;